import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BudgetChart from './budget-chart';
import {
  currentMonth,
  formatCurrency,
  getFullName,
  getUserInitials,
  handleApiError
} from 'utils/functions';
import InsightEmptystate from 'assets/images/insight-empty-state.png';
import { DateRangeType } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import { DashboardMonthlyStatsType } from 'models/statements.model';

type Props = {
  dateRange: DateRangeType;
};

const Insight = ({ dateRange }: Props) => {
  const { activeCompany, monthlyStat } = useDashboardContext();
  const [stat, setStat] = useState<DashboardMonthlyStatsType>(monthlyStat);
  const [budget, setBudget] = useState('');

  const { monthly_spent: spent, monthly_gift: gift, monthly_balance: balance, top_spenders } = stat;

  const data = [
    {
      name: 'Amount Budgeted',
      value: (
        <p>
          <small className='text-[10px] font-400 px-1'>NGN</small>
          {formatCurrency(budget, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            style: 'decimal'
          })}
        </p>
      ),
      color: '#E60A2B'
    },
    {
      name: 'Money Spent',
      value: (
        <p className=''>
          <small className='text-[10px] font-400 px-1'>NGN</small>
          {formatCurrency(spent, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            style: 'decimal'
          })}
        </p>
      ),
      color: '#2E3031'
    },
    {
      name: 'Gifts',
      value: (
        <p>
          <small className='text-[10px] font-400 px-1'>NGN</small>
          {formatCurrency(gift, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            style: 'decimal'
          })}
        </p>
      ),
      color: '#0080E5'
    },
    {
      name: 'Balance',
      value: (
        <p>
          <small className='text-[10px] font-400 px-1'>NGN</small>
          {formatCurrency(balance, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            style: 'decimal'
          })}
        </p>
      ),
      color: '#EFF0F0'
    }
  ];

  const start_date = dateRange.date.startDate.toISOString();
  const end_date = dateRange.date.endDate.toISOString();
  const budgetValue = parseInt(budget || '0');
  const spentValue = parseInt(spent || '0');
  const giftValue = parseInt(gift || '0');
  const balanceValue = parseInt(balance || '0');
  const isDataEmpty =
    budgetValue === 0 && spentValue === 0 && giftValue === 0 && balanceValue === 0;
  const total =
    parseInt(budget ?? '0') +
    parseInt(spent ?? '0') +
    parseInt(gift ?? '0') +
    parseInt(balance ?? '0');
  const budgetPercentage = (parseInt(budget ?? '0') / total) * 100;
  const balancePercentage = (parseInt(balance ?? '0') / total) * 100;
  const giftPercentage = (parseInt(gift ?? '0') / total) * 100;
  const spentPercentage = (parseInt(spent ?? '0') / total) * 100;

  const loadStat = async () => {
    try {
      const params = { start_date, end_date, company_id: activeCompany.id };
      const res = await apiService.get('admin/dashboard', { params });
      setStat(res.data.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadBudget = async () => {
    try {
      const params = { start_date, end_date };
      const res = await apiService.get(`admin/budget/${activeCompany.id}`, { params });
      setBudget(res.data.data.total_budget);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (!activeCompany?.id) return;
    loadStat();
    loadBudget();
  }, [activeCompany, dateRange]);

  return (
    <div className='h-full border-[0.8px] border-[#EFF0F0] py-[24px] px-[20px]'>
      <div className='flex justify-between items-center mb-4'>
        <p className='font-medium'>{`${dateRange.range}'s Insights`}</p>
        {isDataEmpty ? (
          <div />
        ) : (
          <Link
            to={`/monthly-insight?start_date=${start_date}&end_date=${end_date}&range=${dateRange.range}`}
            className='text-xs text-red-5'>
            View breakdown
          </Link>
        )}
      </div>

      {isDataEmpty ? (
        <div className='flex flex-col items-center justify-center h-fit text-[#6F7376] text-sm p-2 mt-6'>
          <img src={InsightEmptystate} alt='empty-state' />
          <p className='my-3'>
            There were no transactions recorded in {currentMonth}. Let&apos;s aim for a more active
            month.
          </p>
        </div>
      ) : (
        <div>
          <div>
            <BudgetChart
              budget={budgetPercentage.toString() ?? '0'}
              balance={balancePercentage.toString() ?? '0'}
              gift={giftPercentage.toString() ?? '0'}
              spent={spentPercentage.toString() ?? '0'}
            />
          </div>
          <div>
            {data.map(({ name, value, color }, index) => (
              <div key={index} className='flex justify-between py-[7px]'>
                <div className='flex gap-4 items-center'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='6'
                    height='7'
                    viewBox='0 0 6 7'
                    fill='none'>
                    <circle cx='3' cy='3.5' r='3' fill={color} />
                  </svg>
                  <p className='font-[#5E6164] text-[12px]'>{name}</p>
                </div>
                <p className='text-[16px]'>{value}</p>
              </div>
            ))}
          </div>
          <div className='mt-7'>
            <p className='text-sm text-[#2E3031] mb-[14px]'>Top Spenders</p>
            <div className='flex justify-between gap-3 flex-wrap w-full'>
              {top_spenders?.slice(0, 4).map(({ user, total_spent }, index) => (
                <div key={index} className='w-fit flex flex-col justify-center items-center'>
                  <div className='bg-[#EBF6FF] rounded-full p-[15px] w-[54px] h-[54px] font-[500] text-[#6F7376]'>
                    {getUserInitials(user)}
                  </div>

                  <p className='text-[#5E6164] text-[12px] py-2 text-center'>{getFullName(user)}</p>
                  <p className='text-xs'>{formatCurrency(total_spent)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Insight;
